import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Ban, BadgeCheck, Bot, Check, ChevronLeft, ChevronRight, DollarSign, Pencil, UserCheck, UserPlus, Users,
} from "lucide-react";
import { dashboardService } from "../../services/api";
import { fmtDateTimezone } from "../utils/dateUtils/fmtDate";
import StatCard from "../../components/StatCard/StatCard";
import StatusPill from "../../components/StatusPill/StatusPill";
import { useNotification } from "../../components/Notifications/NotificationContext";
import useUrlFilters from "./useUrlFilters";
import AdminFilters from "./AdminFilters";
import { AdminMetrics } from "./AdminMetrics";

const fmtBRL = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const FILTER_KEYS = ["start_date", "end_date", "username", "email"];
const SEARCHES = [
    { key: "username", placeholder: "Username" },
    { key: "email", placeholder: "E-mail" },
];

function UserRow({ user, editingFee, onToggleFee, onSaveFee, onOpenBots, onBan, onUnban }) {
    const banned = !user.is_active;
    return (
        <article className="adm-row">
            <div className="adm-ident">
                <div className="adm-avatar">{(user.username || "?").charAt(0)}</div>
                <div className="adm-ident-text">
                    <div className="adm-title">
                        <span className="adm-name">{user.username}</span>
                        <span className="adm-muted">#{user.id}</span>
                        <StatusPill color={banned ? "red" : "green"}>{banned ? "Banido" : "Ativo"}</StatusPill>
                        {user.is_admin && <span className="adm-tag">Admin</span>}
                        {user.ip_banned && <span className="adm-tag adm-tag--warn">IP banido</span>}
                    </div>
                    <div className="adm-sub">{user.email}</div>
                    <div className="adm-sub">Desde {fmtDateTimezone(user.created_at, "date")}</div>
                    {(user.gateways || []).length > 0 && (
                        <div className="adm-tags">
                            {user.gateways.map((g) => <span key={g} className="adm-tag">{g}</span>)}
                        </div>
                    )}
                </div>
            </div>

            <dl className="adm-metrics">
                <div>
                    <dt>Faturamento</dt>
                    <dd>{fmtBRL(user.faturamento)}</dd>
                    <small>{user.vendas || 0} vendas</small>
                </div>
                <div>
                    <dt>Comissão</dt>
                    <dd>{fmtBRL(user.comissao)}</dd>
                </div>
                <div>
                    <dt>Bots · Flows</dt>
                    <dd>{user.bots_count || 0} · {user.flows_count || 0}</dd>
                </div>
                <div>
                    <dt>Taxa</dt>
                    {editingFee ? (
                        <form
                            className="adm-fee-form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                onSaveFee(new FormData(e.target).get("fee"));
                            }}
                        >
                            <input type="number" name="fee" step="0.01" defaultValue={user.fee ?? 0.5} autoFocus />
                            <button type="submit" className="adm-icon-btn" aria-label="Salvar taxa" title="Salvar taxa">
                                <Check size={15} />
                            </button>
                        </form>
                    ) : (
                        <dd>{fmtBRL(user.fee)}</dd>
                    )}
                </div>
            </dl>

            <div className="adm-actions">
                <button type="button" className="adm-btn" title="Ver os bots deste usuário" onClick={onOpenBots}>
                    <Bot size={14} /> Ver bots
                </button>
                <button type="button" className="adm-btn" title="Alterar taxa" onClick={onToggleFee}>
                    <Pencil size={14} /> Taxa
                </button>
                {banned ? (
                    <button type="button" className="adm-btn" title="Desbanir usuário" onClick={onUnban} disabled={user.is_admin}>
                        <UserCheck size={14} /> Desbanir
                    </button>
                ) : (
                    <button type="button" className="adm-btn adm-btn--danger" title="Banir usuário (bloqueia bots e IP)"
                        onClick={onBan} disabled={user.is_admin}>
                        <Ban size={14} /> Banir
                    </button>
                )}
            </div>
        </article>
    );
}

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingFeeId, setEditingFeeId] = useState(null);
    const { showError, showSuccess } = useNotification();
    const navigate = useNavigate();
    // Filtros na URL; o período é o do cadastro do usuário.
    const [filters, applyFilters, filtersKey] = useUrlFilters(FILTER_KEYS);

    const [metrics, setMetrics] = useState({
        total_users: 0,
        new_users_count: 0,
        users_with_payments_count: 0,
        users_with_approved_payments_count: 0,
    });

    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadUsers(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey]);

    const loadUsers = async (page = 1) => {
        try {
            setLoading(true);

            const { data } = await dashboardService.getAdminUsers({
                page,
                ...filters,
            });

            setUsers(data.users || []);
            setMetrics({
                total_users: data.total_users,
                new_users_count: data.new_users_count,
                users_with_payments_count: data.users_with_payments_count,
                users_with_approved_payments_count:
                    data.users_with_approved_payments_count,
            });

            setPagination(data.pagination || {});
            setCurrentPage(page);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFeeChange = async (userId, newFee) => {
        try {
            const result = await dashboardService.updateUserFee(userId, { fee: newFee });
            setEditingFeeId(null);
            loadUsers(currentPage);
            if (result.data.success) {
                showSuccess("Taxa atualizada com sucesso!");
            }
        } catch (err) {
            console.error(err);
            showError("Erro ao atualizar taxa. " + (err?.response?.data?.error || err?.message || ''));
        }
    };

    const handleBan = async (user) => {
        const ok = window.confirm(
            `Banir ${user.username}?\n\nIsso vai:\n• Derrubar todas as sessões dele\n• Bloquear todos os bots dele\n• Banir os IPs conhecidos dele (impede novo cadastro)`
        );
        if (!ok) return;

        try {
            const { data } = await dashboardService.banUser(user.id);
            showSuccess(
                `Usuário banido: ${data.bots_blocked} bots bloqueados, ${data.ips_banned} IPs banidos.`
            );
            loadUsers(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao banir. " + (err?.response?.data?.error || err?.message || ''));
        }
    };

    const handleUnban = async (user) => {
        const ok = window.confirm(
            `Desbanir ${user.username}?\n\nEle volta a acessar a plataforma. Os bots dele continuam bloqueados (desbloqueie na aba Bots).`
        );
        if (!ok) return;

        try {
            await dashboardService.unbanUser(user.id);
            showSuccess("Usuário desbanido. Os bots dele permanecem bloqueados.");
            loadUsers(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao desbanir. " + (err?.response?.data?.error || err?.message || ''));
        }
    };

    return (
        <>
            <AdminFilters values={filters} onApply={applyFilters} period searches={SEARCHES} />

            <AdminMetrics>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard title="Usuários cadastrados" value={metrics.total_users} icon={{ Icon: Users, color: 'blue' }} />
                    <StatCard title="Novos (filtro)" value={metrics.new_users_count} icon={{ Icon: UserPlus, color: 'green' }} />
                    <StatCard
                        title="Usuários com pagamentos"
                        value={metrics.users_with_payments_count}
                        icon={{ Icon: DollarSign, color: 'purple' }}
                    />
                    <StatCard
                        title="Usuários com pagamentos aprovados"
                        value={metrics.users_with_approved_payments_count}
                        icon={{ Icon: BadgeCheck, color: 'green' }}
                    />
                </div>
            </AdminMetrics>

            {/* LISTA DE USUÁRIOS */}
            <div className="adm-list">
                {users.map((user) => (
                    <UserRow
                        key={user.id}
                        user={user}
                        editingFee={editingFeeId === user.id}
                        onToggleFee={() => setEditingFeeId(editingFeeId === user.id ? null : user.id)}
                        onSaveFee={(fee) => handleFeeChange(user.id, fee)}
                        onOpenBots={() => {
                            const q = new URLSearchParams({ owner_id: user.id, owner_label: user.username });
                            navigate(`/dashboard/admin/bots?${q}`);
                        }}
                        onBan={() => handleBan(user)}
                        onUnban={() => handleUnban(user)}
                    />
                ))}
            </div>

            {users.length === 0 && !loading && (
                <div className="empty-state">
                    <Users size={28} />
                    <h4>Nenhum usuário encontrado</h4>
                </div>
            )}

            {/* PAGINAÇÃO */}
            {!loading && users.length > 0 && (
                <div className="mt-8 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Página {currentPage} de {pagination.pages} (
                        {pagination.total} usuários)
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => loadUsers(currentPage - 1)}
                            disabled={!pagination.has_prev}
                            className="btn-secondary inline-flex items-center gap-1"
                        >
                            <ChevronLeft size={16} /> Anterior
                        </button>

                        <button
                            onClick={() => loadUsers(currentPage + 1)}
                            disabled={!pagination.has_next}
                            className="btn-primary inline-flex items-center gap-1">
                            Próximo <ChevronRight size={16} />

                        </button>
                    </div>
                </div>
            )
            }
        </>
    );
}
